import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  PlusIcon,
  StackIcon,
  InfoIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  DimensionType,
  DIMENSION_LABELS,
  DimensionGroupingConfig,
  MultiYearForecastData,
} from '@/types/rebateAnalytics';
import { UnifiedForecastResult } from '@erp/shared-types';
import { useBreakdownCells } from './hooks/useBreakdownCells';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '../ui/breakdown-tables/BreakdownTable';
import {
  createDataCellRenderer,
  aggregateCellValuesWithYOY,
} from './utils/rebateAnalyticsUtils';
import { createMonthMetadata } from '@/utils/rebateForecastingUtils';
import {
  generateMonthColumnOrder,
  generateMonthSubColumnOrder,
} from '@/utils/breakdown-transforms/monthOrdering';
import { DimensionGroupingModal } from './DimensionGroupingModal';
import { generateTableName } from '@/utils/rebateAnalyticsTableUtils';

interface RebateAnalyticsTableConfigFormProps {
  primaryXDimension: DimensionType;
  secondaryXDimension: DimensionType | undefined;
  primaryYDimension: DimensionType | undefined;
  secondaryYDimension: DimensionType | undefined;
  onSecondaryXDimensionChange: (value: DimensionType | undefined) => void;
  onPrimaryYDimensionChange: (value: DimensionType | undefined) => void;
  onSecondaryYDimensionChange: (value: DimensionType | undefined) => void;
  autoGeneratedName: string;
  customName?: string;
  onCustomNameChange?: (name: string) => void;
  hasDimensionChanged?: boolean;
  isValidName?: boolean;
  hasDuplicates: boolean;
  showPreview?: boolean;
  forecastData: UnifiedForecastResult | null;
  multiYearForecastData?: MultiYearForecastData;
  supplierIdToName?: Map<string, string>;
  divisionIdToName?: Map<string, string>;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
  ruleTagsMap?: Record<string, string[]>;
  agreementTagsMap?: Record<string, string[]>;
  supplierTagsMap?: Record<string, string[]>;
  buttonLabel?: string;
  buttonIcon?: React.ReactNode;
  onSave: () => void;
  onCancel?: () => void;
  // Dimension grouping props (supports all dimensions)
  ruleIdToName?: Map<string, string>;
  agreementIdToName?: Map<string, string>;
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >;
  onDimensionGroupingConfigChange?: (
    dimensionType: Exclude<
      DimensionType,
      DimensionType.TIME | DimensionType.RULE_TYPE
    >,
    config: DimensionGroupingConfig | undefined,
  ) => void;
}

/**
 * Reusable form fields component for table configuration
 */
function RebateAnalyticsTableConfigForm({
  primaryXDimension,
  secondaryXDimension,
  primaryYDimension,
  secondaryYDimension,
  onSecondaryXDimensionChange,
  onPrimaryYDimensionChange,
  onSecondaryYDimensionChange,
  autoGeneratedName,
  customName,
  onCustomNameChange,
  hasDimensionChanged = false,
  isValidName = true,
  hasDuplicates,
  showPreview = false,
  forecastData,
  multiYearForecastData,
  supplierIdToName,
  divisionIdToName,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  ruleTagsMap,
  agreementTagsMap,
  supplierTagsMap,
  buttonLabel = 'Create',
  buttonIcon = <PlusIcon className="h-4 w-4" />,
  onSave,
  onCancel,
  ruleIdToName,
  agreementIdToName,
  dimensionGroupingConfig,
  onDimensionGroupingConfigChange,
}: RebateAnalyticsTableConfigFormProps) {
  // Modal state for dimension grouping editors - one per dimension type
  // RULE_TYPE is excluded because it doesn't support grouping
  const [openModalForDimension, setOpenModalForDimension] = useState<Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  > | null>(null);
  // Get all dimensions except TIME (Month)
  const allDimensionsExceptTime = Object.values(DimensionType).filter(
    (dim) => dim !== DimensionType.TIME,
  );

  // Helper function to check if a dimension is already used (duplicate)
  const isDimensionUsed = (dimension: DimensionType): boolean => {
    const selectedDimensions = [
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
    ].filter((d): d is DimensionType => d !== undefined);

    return selectedDimensions.filter((d) => d === dimension).length > 1;
  };

  // Helper function to check if a dimension is selected elsewhere
  const isDimensionSelectedElsewhere = (
    dimension: DimensionType,
    currentSelector: 'secondaryX' | 'primaryY' | 'secondaryY',
  ): boolean => {
    const otherSelections: DimensionType[] = [];
    if (currentSelector !== 'secondaryX') {
      if (secondaryXDimension) otherSelections.push(secondaryXDimension);
    }
    if (currentSelector !== 'primaryY') {
      if (primaryYDimension) otherSelections.push(primaryYDimension);
    }
    if (currentSelector !== 'secondaryY') {
      if (secondaryYDimension) otherSelections.push(secondaryYDimension);
    }
    // Always include primaryX (TIME) but it's locked so not a duplicate concern

    return otherSelections.includes(dimension);
  };

  // Generate preview data if needed (hooks must be called unconditionally)
  const previewData = useBreakdownCells({
    multiYearForecastData: multiYearForecastData || {
      currentYear: new Date().getFullYear(),
      currentYearData: null,
      previousYearData: null,
      isCumulative: false,
    },
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
    dimensionGroupingConfig,
  });

  // Create month metadata from forecast data for cell renderer
  const monthMetadata = useMemo(() => {
    if (!multiYearForecastData?.currentYearData) {
      return [];
    }
    return createMonthMetadata(multiYearForecastData.currentYearData);
  }, [multiYearForecastData]);

  // Create cell renderer adapter
  const previewRenderDataCell = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    const currentYear =
      multiYearForecastData?.currentYear || new Date().getFullYear();
    const previousYear = currentYear - 1;
    const originalRenderer = createDataCellRenderer(
      monthMetadata,
      currentYear,
      previousYear,
    );

    return (context: BreakdownCellContext) => {
      const { cell } = context;
      const metadata = cell.metadata as
        | {
            headerName?: string;
            isGroupLevel?: boolean;
          }
        | undefined;

      // Create mock old-style context for compatibility
      const mockOldContext = {
        header: {
          name: metadata?.headerName || cell.column,
        },
        row: metadata?.isGroupLevel
          ? undefined
          : {
              label: cell.subRow || '',
              values: { [metadata?.headerName || '']: cell.value },
            },
        group: {
          label: cell.row,
          values: metadata?.isGroupLevel
            ? { [metadata.headerName || '']: cell.value }
            : undefined,
        },
        isGroupLevel: metadata?.isGroupLevel ?? false,
      };

      const legacyValue = cell.value as
        | number
        | { current: number; previous: number; delta: number }
        | null;
      return originalRenderer(legacyValue, mockOldContext);
    };
  }, [monthMetadata, showPreview, previewData, multiYearForecastData]);

  // Get dimension labels for preview
  const previewGroupLabel = primaryYDimension
    ? DIMENSION_LABELS[primaryYDimension]
    : undefined;
  const previewSubRowLabel = secondaryYDimension
    ? DIMENSION_LABELS[secondaryYDimension]
    : undefined;

  // Generate month ordering if primary X dimension is TIME
  const xDimOrder = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    if (primaryXDimension === DimensionType.TIME) {
      return generateMonthColumnOrder(previewData);
    }
    return undefined;
  }, [primaryXDimension, previewData, showPreview]);

  const xDim2Order = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    if (secondaryXDimension === DimensionType.TIME) {
      return generateMonthSubColumnOrder(previewData);
    }
    return undefined;
  }, [secondaryXDimension, previewData, showPreview]);

  // Compute initial expanded rows/columns for preview
  const initialExpandedRows = useMemo(() => {
    if (!showPreview || previewData.length === 0) return new Set<string>();
    // Get first unique row value
    const firstRow = previewData.find((c) => c.subRow === null)?.row;
    if (firstRow) {
      return new Set([firstRow]);
    }
    return new Set<string>();
  }, [previewData, showPreview]);

  const initialExpandedColumns = useMemo(() => {
    if (!showPreview || !secondaryXDimension || previewData.length === 0) {
      return new Set<string>();
    }
    // Get first unique column value
    const firstColumn = previewData.find((c) => c.subColumn === null)?.column;
    if (firstColumn) {
      return new Set([firstColumn]);
    }
    return new Set<string>();
  }, [showPreview, secondaryXDimension, previewData]);

  // Check if we're in the default state (only TIME selected, all others are defaults/none)
  const isDefaultState = useMemo(() => {
    return (
      secondaryXDimension === undefined &&
      primaryYDimension === undefined &&
      secondaryYDimension === undefined
    );
  }, [secondaryXDimension, primaryYDimension, secondaryYDimension]);

  // Derived: check if name is custom
  const isCustomName = useMemo(() => {
    return customName !== undefined && customName !== autoGeneratedName;
  }, [customName, autoGeneratedName]);

  // Check if reset button should be shown
  // Show whenever name is custom and differs from auto-generated (regardless of dimension changes)
  const showResetButton = useMemo(() => {
    return (
      isCustomName &&
      customName !== undefined &&
      customName.trim() !== '' &&
      customName !== autoGeneratedName
    );
  }, [isCustomName, customName, autoGeneratedName]);

  // Handle reset to auto-generated name
  const handleResetToAutoGen = () => {
    if (onCustomNameChange) {
      onCustomNameChange(autoGeneratedName);
    }
  };

  // Check if form is valid (primaryYDimension is required and name is valid)
  const isFormValid = useMemo(() => {
    return !hasDuplicates && primaryYDimension !== undefined && isValidName;
  }, [hasDuplicates, primaryYDimension, isValidName]);

  return (
    <div className="space-y-4 w-full min-w-0 max-w-full">
      {/* Table Name - full width */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Table Name</Label>
        <Input
          value={customName ?? autoGeneratedName}
          onChange={(e) => {
            if (onCustomNameChange) {
              onCustomNameChange(e.target.value);
            }
          }}
          placeholder={autoGeneratedName}
          className={cn(
            'w-full',
            !isValidName && 'border-destructive focus-visible:ring-destructive',
          )}
        />
        {!isValidName && (
          <p className="text-sm text-destructive">
            Table name cannot be empty or contain only whitespace
          </p>
        )}
        {showResetButton && (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={handleResetToAutoGen}
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          >
            Use: {autoGeneratedName}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 space-y-3">
          {/* X Axis Row */}
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 w-30">
                <Label className="text-sm font-medium">Column Axis:</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium mb-1">Column Axis (X)</p>
                    <p className="text-xs">
                      Columns display Time (required) and optionally a second
                      dimension for nested columns. For example: Time × Division
                      creates columns grouped by month, then subdivided by
                      division.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select value={primaryXDimension} disabled>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DimensionType.TIME}>
                    {DIMENSION_LABELS[DimensionType.TIME]}
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="h-7 w-6"></div>
              <span className="text-muted-foreground text-sm">×</span>
              <div className="flex items-center gap-2">
                <Select
                  value={secondaryXDimension || 'none'}
                  onValueChange={(value) =>
                    onSecondaryXDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      secondaryXDimension &&
                        isDimensionUsed(secondaryXDimension) &&
                        'border-destructive text-destructive',
                    )}
                  >
                    <SelectValue placeholder="None (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (optional)</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'secondaryX')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secondaryXDimension &&
                secondaryXDimension !== DimensionType.TIME &&
                secondaryXDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(secondaryXDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[secondaryXDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[secondaryXDimension]
                          ? 'Edit'
                          : 'Configure'}{' '}
                        {DIMENSION_LABELS[secondaryXDimension]} grouping
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
            </div>
            {secondaryXDimension ? (
              <p className="text-xs text-muted-foreground italic pl-34">
                → Columns: Time × {DIMENSION_LABELS[secondaryXDimension]}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic pl-34">
                → Columns: Time
              </p>
            )}
          </div>

          {/* Y Axis Row */}
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 w-30">
                <Label className="text-sm font-medium">Row Axis:</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium mb-1">Row Axis (Y)</p>
                    <p className="text-xs">
                      Rows require a primary dimension (e.g., Division,
                      Supplier). Add a secondary dimension for sub-rows within
                      each group. For example: Division × Supplier creates rows
                      grouped by division, with suppliers as sub-rows.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={primaryYDimension || 'none'}
                  onValueChange={(value) =>
                    onPrimaryYDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      primaryYDimension &&
                        isDimensionUsed(primaryYDimension) &&
                        'border-destructive text-destructive',
                    )}
                  >
                    <SelectValue placeholder="None (Required)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Required)</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'primaryY')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {primaryYDimension &&
                primaryYDimension !== DimensionType.TIME &&
                primaryYDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(primaryYDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[primaryYDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[primaryYDimension]
                          ? 'Edit'
                          : 'Configure'}{' '}
                        {DIMENSION_LABELS[primaryYDimension]} grouping
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
              <span className="text-muted-foreground text-sm">×</span>
              <div className="flex items-center gap-2">
                <Select
                  value={secondaryYDimension || 'none'}
                  onValueChange={(value) =>
                    onSecondaryYDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                  disabled={!primaryYDimension}
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      secondaryYDimension &&
                        isDimensionUsed(secondaryYDimension) &&
                        'border-destructive text-destructive',
                      !primaryYDimension && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <SelectValue placeholder="None (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (optional)</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'secondaryY')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secondaryYDimension &&
                secondaryYDimension !== DimensionType.TIME &&
                secondaryYDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(secondaryYDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[secondaryYDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[secondaryYDimension]
                          ? 'Edit'
                          : 'Configure'}{' '}
                        {DIMENSION_LABELS[secondaryYDimension]} grouping
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
            </div>
            {primaryYDimension && (
              <p className="text-xs text-muted-foreground italic pl-34">
                → Rows: {DIMENSION_LABELS[primaryYDimension]}
                {secondaryYDimension &&
                  ` × ${DIMENSION_LABELS[secondaryYDimension]}`}
              </p>
            )}
          </div>

          {/* Dimension Grouping Info Box */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-md border border-border/50">
            <StackIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-600" />
            <div className="space-y-0.5">
              <p className="font-medium text-foreground">Dimension Grouping</p>
              <p>
                Click the{' '}
                <StackIcon className="h-3 w-3 inline text-purple-600" /> icon
                next to a dimension to organize items into custom groups. For
                example, group multiple suppliers into &quot;Tier 1
                Suppliers&quot; or &quot;Strategic Partners&quot;.
              </p>
            </div>
          </div>

          {/* Error message for duplicates */}
          {hasDuplicates && (
            <AlertContainer variant="destructive" className="py-2">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Duplicate dimensions detected:{' '}
                {[
                  primaryXDimension,
                  secondaryXDimension,
                  primaryYDimension,
                  secondaryYDimension,
                ]
                  .filter((d): d is DimensionType => d !== undefined)
                  .reduce((dups, dim, idx, arr) => {
                    if (arr.indexOf(dim) !== idx && !dups.includes(dim)) {
                      dups.push(dim);
                    }
                    return dups;
                  }, [] as DimensionType[])
                  .map((d) => DIMENSION_LABELS[d])
                  .join(', ')}
                . Each dimension can only be used once.
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Dimension Grouping Modal */}
          <DimensionGroupingModal
            open={!!openModalForDimension}
            onOpenChange={(open) => {
              if (!open) {
                setOpenModalForDimension(null);
              }
            }}
            dimensionType={openModalForDimension}
            dimensionGroupingConfig={dimensionGroupingConfig}
            onDimensionGroupingConfigChange={onDimensionGroupingConfigChange}
            supplierIdToName={supplierIdToName}
            divisionIdToName={divisionIdToName}
            ruleIdToName={ruleIdToName}
            agreementIdToName={agreementIdToName}
            ruleTagIdToName={ruleTagIdToName}
            agreementTagIdToName={agreementTagIdToName}
            supplierTagIdToName={supplierTagIdToName}
          />
        </div>

        {/* Action Button */}
        <div className="flex items-start gap-3">
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    size="sm"
                    onClick={onSave}
                    className="gap-2"
                    disabled={!isFormValid}
                  >
                    {buttonIcon}
                    {buttonLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              {!isFormValid && (
                <TooltipContent>
                  <p>
                    {!primaryYDimension
                      ? 'Please select a row axis to create the table'
                      : hasDuplicates
                        ? 'Please remove duplicate dimensions'
                        : ''}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
            {onCancel && (
              <Button size="sm" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {showPreview &&
        !hasDuplicates &&
        !isDefaultState &&
        forecastData &&
        previewData.length > 0 &&
        previewRenderDataCell && (
          <div className="mt-4 pt-4 border-t">
            <Label className="text-sm font-medium text-muted-foreground mb-2 block">
              Preview
            </Label>
            <div className="opacity-60 pointer-events-none max-w-full max-h-[800px] overflow-hidden">
              <BreakdownTable
                data={previewData}
                rowLabel={previewGroupLabel}
                subRowLabel={previewSubRowLabel}
                columnLabel={DIMENSION_LABELS[primaryXDimension]}
                subColumnLabel={
                  secondaryXDimension
                    ? DIMENSION_LABELS[secondaryXDimension]
                    : undefined
                }
                title=""
                subtitle=""
                showCard={false}
                renderCell={previewRenderDataCell}
                aggregateCellValues={aggregateCellValuesWithYOY}
                showXDim1Total={true}
                showYDim1Total={true}
                maxHeight={400}
                initialExpandedRows={initialExpandedRows}
                initialExpandedColumns={initialExpandedColumns}
                showColumnReferences={true}
                xDimOrder={xDimOrder}
                xDim2Order={xDim2Order}
              />
            </div>
          </div>
        )}
    </div>
  );
}

export default RebateAnalyticsTableConfigForm;
